import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type {
  AccessLevel,
  CatalogSelectOption,
  ExistingAccessLevel,
  ShareUser,
  TranslateFn,
} from "../types";
import {
  getUserEmail,
  getUserInitial,
  getUserName,
  toTestId,
} from "../utils/catalog";

type SharedUsersListProps = {
  t: TranslateFn;
  users: ShareUser[];
  loading: boolean;
  currentUserId?: string;
  accessLevels: CatalogSelectOption<AccessLevel>[];
  updatingShare: Record<string, boolean>;
  onAccessChange: (user: ShareUser, accessLevel: ExistingAccessLevel) => void;
};

const SharedUsersList = ({
  t,
  users,
  loading,
  currentUserId,
  accessLevels,
  updatingShare,
  onAccessChange,
}: SharedUsersListProps) => {
  if (loading) {
    return (
      <Box className="flex items-center justify-center py-8">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!users.length) {
    return (
      <Typography className="text-sm text-[var(--muted-foreground)]">
        {t("aiCatalog.shareDialog.notSharedYet")}
      </Typography>
    );
  }

  return (
    <Box className="space-y-3">
      {users.map((user) => {
        const email = getUserEmail(user);

        return (
          <Box
            key={user.id || email}
            className="flex items-center justify-between gap-4"
          >
            <Box className="flex items-center gap-3 min-w-0">
              <Box className="w-5 h-5 rounded-full bg-[var(--secondary)] flex items-center justify-center shrink-0">
                <Typography className="text-[10px] text-[var(--secondary-foreground)]">
                  {getUserInitial(user, t("aiCatalog.shareDialog.unknownUser"))}
                </Typography>
              </Box>
              <Box className="min-w-0">
                <Typography className="text-sm text-[var(--foreground)] truncate">
                  {getUserName(user, t("aiCatalog.shareDialog.unknownUser"))}
                </Typography>
                <Typography className="text-sm text-[var(--muted-foreground)] truncate">
                  {email}
                </Typography>
              </Box>
            </Box>
            {user.user_id === currentUserId ? null : (
              <Select
                data-testid={`ai-catalog-share-existing-access-${toTestId(email)}`}
                value={user.access_level || user.role || "viewer"}
                onChange={(event: SelectChangeEvent<ExistingAccessLevel>) =>
                  onAccessChange(
                    user,
                    event.target.value as ExistingAccessLevel,
                  )
                }
                IconComponent={KeyboardArrowDownIcon}
                disabled={!!updatingShare[email]}
                className="h-10 min-w-[120px] rounded-[8px] bg-[var(--muted)] text-[var(--foreground)] [&_fieldset]:border-0 [&_.MuiSelect-select]:px-[10px] [&_.MuiSelect-select]:py-2 [&_.MuiSelect-select]:text-sm [&_.MuiSvgIcon-root]:text-[var(--foreground)]"
              >
                {accessLevels.map((option) => (
                  <MenuItem
                    data-testid={`ai-catalog-share-existing-access-option-${toTestId(email)}-${option.value}`}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem
                  data-testid={`ai-catalog-share-existing-access-option-${toTestId(email)}-remove`}
                  key="remove"
                  value="remove"
                >
                  {t("common.remove")}
                </MenuItem>
              </Select>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SharedUsersList;
